import React, { useState, useEffect, useRef } from "react";
import { useState as useStateReact } from "react";
import { AnimatePresence } from "framer-motion";
import PhotoViewer from "./components/Gallery";
import {
  Phone,
  MessageCircle,
  Wifi,
  Car,
  Utensils,
  Waves,
  TreePine,
  Coffee,
  Bath,
  Tv,
  Wind,
  MapPin,
  PartyPopper,
  Users,
  PawPrint,
  Calendar,
  Home,
  Camera,
  Gamepad2,
  ChevronRight,
  Flame,
  Sparkles,
  Flower2,
  Flower,
  Leaf,
  Wine,
  CircleQuestionMark,
  Globe,
  FacebookIcon,
  InstagramIcon,
} from "lucide-react";

import movie from "./images/movie.webp";
import pool from "./images/pool.webp";
import kitchen from "./images/kitchen.webp";
import bbq from "./images/bbq.webp";
import snack from "./images/snack.webp";
import fornolegna from "./images/fornolegna.webp";

import pink from "./images/rooms/rosa.webp";
import brown from "./images/rooms/marrone.webp";
import cyan from "./images/rooms/azzurra.webp";
import green from "./images/rooms/verde.webp";

import fromWindow from "./images/from_window.webp";
import windowFrame from "./images/window.png";

import main from "./images/main.webp";

import bricks from "./images/bricks2.webp";

import theme from "./theme";
import { Parallax, ParallaxProvider } from "react-scroll-parallax";

import ServiceCard from "./components/ServiceCard";

import HeroCarousel from "./components/HeroCarousel";

import RoomCard from "./components/roomCard";

import Footer from "./components/footer";
import StickyHeading from "./components/StickyHeading";
import { useTranslation } from "react-i18next";

import ActivitiesSection from "./sections/activities";

// Custom styles
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lora:wght@400;500&family=Open+Sans:wght@300;400;500&display=swap');

  :root {
    --sage: ${theme.sage};
    --cream: ${theme.cream};
    --dusty-pink: ${theme.dustyPink};
    --warm-brown: ${theme.warmBrown};
    --light-sage: ${theme.lightSage};
    --wheat: ${theme.wheat};
    --moss: ${theme.moss};
    --blush: ${theme.blush};
  }

  body {
    background-color: var(--cream);
    font-family: 'Open Sans', sans-serif;
  }

  h1, h2, h3 {
    font-family: 'Playfair Display', serif;
  }

  .font-lora {
    font-family: 'Lora', serif;
  }

  /* Decorative dots pattern */
  .dots-pattern {
    background-image: url(${bricks});
    background-size: 200px 200px;
    opacity: 0.4;
  }

  /* Hanging sign used for section headings */
  .hanging-sign {
    display: inline-block;
    padding: 2rem 3rem;
    border: 4px solid var(--warm-brown);
    background-color: var(--cream);
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    position: relative;
  }
  .hanging-sign::before,
  .hanging-sign::after {
    content: '';
    position: absolute;
    top: -100px;
    width: 10px;
    height: 100px;
    background-color: var(--warm-brown);
  }
  .hanging-sign::before { left: 20%; }
  .hanging-sign::after  { right: 20%; }
`;

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > window.innerHeight / 2);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setIsLangOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { t } = useTranslation();
  const { i18n } = useTranslation();
  // Use the resolved language (or fall back to i18n.language) and strip region codes like "en‑US" → "en"
  const baseLang = (i18n.resolvedLanguage || i18n.language || "").split("-")[0];

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    setIsMenuOpen(false);
  };

  return (
    <>
      <style>{styles}</style>
      <header
        className={`fixed z-[99999] top-0 w-full bg-white/90 backdrop-blur-sm shadow-sm z-50 transition-all duration-500 ${
          hasScrolled ? "translate-y-0" : "-translate-y-full"
        }`}
        style={{ borderBottom: `1px solid ${theme.wheat}` }}
        id="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side - Mobile menu + Logo */}
            <div className="flex items-center gap-3">
              {/* Mobile menu button - now leftmost */}
              <button
                className="md:hidden p-1"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <div className="w-6 h-6 flex flex-col justify-center items-center">
                  <span
                    className={`transition-all duration-300 h-0.5 w-6 ${
                      isMenuOpen ? "rotate-45 translate-y-1.5" : ""
                    }`}
                    style={{ backgroundColor: theme.warmBrown }}
                  ></span>
                  <span
                    className={`transition-all duration-300 h-0.5 w-6 my-1 ${
                      isMenuOpen ? "opacity-0" : ""
                    }`}
                    style={{ backgroundColor: theme.warmBrown }}
                  ></span>
                  <span
                    className={`transition-all duration-300 h-0.5 w-6 ${
                      isMenuOpen ? "-rotate-45 -translate-y-1.5" : ""
                    }`}
                    style={{ backgroundColor: theme.warmBrown }}
                  ></span>
                </div>
              </button>

              {/* Logo */}
              <div className="flex-shrink-0">
                <h1
                  className="text-2xl font-bold"
                  style={{ color: theme.moss }}
                >
                  {t("bnbName")}
                </h1>
                <p className="text-xs" style={{ color: theme.warmBrown }}>
                  {t("bnbTagline")}
                </p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              {["Camere", "Servizi", "Attività", "Contatti"].map((item) => (
                <button
                  key={item}
                  onClick={() =>
                    scrollToSection(item.toLowerCase().replace("à", "a"))
                  }
                  className="font-medium transition-all duration-300 relative group"
                  style={{ color: theme.warmBrown }}
                >
                  <span>{t(item)}</span>
                  <span
                    className="absolute bottom-0 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full"
                    style={{ backgroundColor: theme.sage }}
                  ></span>
                </button>
              ))}
            </nav>

            {/* Right side controls */}
            <div className="flex items-center">
              {/* Language Selector - Now more subtle */}
              <div className="relative mr-3" ref={langRef}>
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-lg transition-all duration-200"
                  style={{
                    backgroundColor: theme.wheat,
                  }}
                  title={`${t("language")}: ${baseLang.toUpperCase()}`}
                >
                  {
                    {
                      it: "🇮🇹",
                      en: "🇬🇧",
                      fr: "🇫🇷",
                      de: "🇩🇪",
                    }[baseLang]
                  }
                </button>
                

                {isLangOpen && (
                  <div
                    className="absolute right-0 mt-2 bg-white rounded-xl shadow-lg border overflow-hidden z-50 min-w-[140px]"
                    style={{ borderColor: theme.wheat }}
                  >
                    {[
                      { code: "it", name: "Italiano", flag: "🇮🇹" },
                      { code: "en", name: "English", flag: "🇬🇧" },
                      { code: "fr", name: "Français", flag: "🇫🇷" },
                      { code: "de", name: "Deutsch", flag: "🇩🇪" },
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          setIsLangOpen(false);
                        }}
                        className={`flex items-center gap-3 w-full px-4 py-3 text-left text-sm transition-all ${
                          baseLang === lang.code ? "font-medium" : ""
                        }`}
                        style={{
                          color: theme.warmBrown,
                          backgroundColor:
                            baseLang === lang.code
                              ? theme.blush
                              : "transparent",
                        }}
                        onMouseEnter={(e) => {
                          if (baseLang !== lang.code) {
                            e.currentTarget.style.backgroundColor = theme.wheat;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (baseLang !== lang.code) {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }
                        }}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="flex-1">{lang.name}</span>
                        {baseLang === lang.code && <span>✓</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Prenota Button - Now more prominent */}
              <button
                onClick={() => scrollToSection("contatti")}
                className="px-4 py-2 rounded-full text-sm font-medium shadow-sm whitespace-nowrap"
                style={{
                  backgroundColor: theme.sage,
                  color: "white",
                }}
              >
                {t("Prenota")}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div
              className="md:hidden py-4"
              style={{ borderTop: `1px solid ${theme.wheat}` }}
            >
              <div className="space-y-3">
                {["Camere", "Servizi", "Attività", "Contatti"].map((item) => (
                  <button
                    key={item}
                    onClick={() =>
                      scrollToSection(item.toLowerCase().replace("à", "a"))
                    }
                    className="block w-full text-left font-medium transition-colors py-2"
                    style={{ color: theme.warmBrown }}
                  >
                    <span>{t(item)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

// Contact Button Component
const ContactButton = ({ type, number }) => {
  const whatsappMessage =
    "Ciao! Vorrei informazioni per una prenotazione al vostro B&B";
  // Sanitize numbers for different URI schemes
  const cleanWaNumber = number.replace(/[^\d]/g, ""); // digits only for wa.me
  const telNumber = number.replace(/\s+/g, ""); // keep '+' but remove spaces for tel:
  const { t } = useTranslation();

  if (type === "whatsapp") {
    return (
      <a
        href={`https://wa.me/${cleanWaNumber}?text=${encodeURIComponent(
          whatsappMessage
        )}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-4 rounded-2xl shadow-sm hover:shadow-md flex items-center justify-center gap-3 text-lg font-medium transform hover:scale-105 transition-all duration-300"
        style={{
          backgroundColor: "#3EC06DFF",
          color: "white",
        }}
      >
        <MessageCircle size={24} />
        <span>{t("btnOpenWhatsApp")}</span>
      </a>
    );
  }else if(type === "facebook") {
    return (
      <a
        href={number}
        target="_blank"
        rel="noopener noreferrer"
        className="p-4 rounded-2xl shadow-sm hover:shadow-md flex items-center justify-center gap-3 text-lg font-medium transform hover:scale-105 transition-all duration-300"
        style={{
          backgroundColor: "#5A7DC7FF",
          color: "white",
        }}
      >
        <FacebookIcon size={24} />
        <span>{t("btnOpenFacebook")}</span>
      </a>
    );
  }else if(type === "instagram") {
    return (
      <a
        href={number}
        target="_blank"
        rel="noopener noreferrer"
        className="p-4 rounded-2xl shadow-sm hover:shadow-md flex items-center justify-center gap-3 text-lg font-medium transform hover:scale-105 transition-all duration-300"
        style={{
          backgroundColor: "#DA5682FF",
          color: "white",
        }}
      >
        <InstagramIcon size={24} />
        <span>{t("btnOpenInstagram")}</span>
      </a>
    );
  }

  return (
    <a
      href={`tel:${telNumber}`}
      className="p-4 rounded-2xl shadow-sm hover:shadow-md flex items-center justify-center gap-3 text-lg font-medium transform hover:scale-105 transition-all duration-300"
      style={{
        backgroundColor: "#E14F4FFF",
        color: "white",
      }}
    >
      <Phone size={24} />
      <span>{t("btnCallNow")}</span>
    </a>
  );
};

// Hero Section Component
const HeroSection = () => {
  const { t } = useTranslation();
  // Dynamically import hero images using require.context
  const heroImages = require
    .context("./images/hero", false, /\.(jpe?g|png|gif|webp)$/)
    .keys()
    .map((file) => require(`./images/hero/${file.replace("./", "")}`));
  return (
    <section
      className="relative h-[100svh] p-3"
      style={{ backgroundColor: theme.cream }}
    >
      <div className="relative flex flex-col h-full w-full justify-end overflow-hidden rounded-xl mx-auto">
        {/* Full‑screen background image */}
        <HeroCarousel imgs={heroImages} />
        {/* Centered text */}
        <div className="relative z-10 flex items-center bg-black/40 justify-center h-full pointer-events-none">
          <div className="text-center px-6">
            <h1
              className="font-light leading-none whitespace-nowrap mb-4"
              style={{
                color: theme.cream,
                fontSize: "clamp(4rem, 8vw, 5rem)",
                lineHeight: 1,
                maxHeight: "5rem",
                overflow: ""
              }}
            >
              {t("heroTitle")}
            </h1>
            <p
              className="text-lg md:text-xl mb-6 text-bold"
              style={{ color: theme.cream }}
            >
              {t("heroSubtitle")}
            </p>

            <button
              onClick={() =>
                document
                  .getElementById("contatti")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:shadow-md pointer-events-auto"
              style={{
                backgroundColor: "transparent",
                color: theme.cream,
                border: `2px solid ${theme.cream}`,
              }}
            >
              {t("btnContactUs")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Overview Section
const ServicesOverview = () => {
  const { t } = useTranslation();
  const poolImages = require.context("./images/pool", true);
  const poolList = poolImages.keys().map((image) => poolImages(image));

  const bbqImages = require.context("./images/bbq_kitchen", true);
  const bbqList = bbqImages.keys().map((image) => bbqImages(image));

  const eventsImages = require.context("./images/events", true);
  const eventsList = eventsImages.keys().map((image) => eventsImages(image));

  const kitchenImages = require.context("./images/kitchen", true);
  const kitchenList = kitchenImages.keys().map((image) => kitchenImages(image));

  const fornoImages = require.context("./images/fornolegna", true);
  const fornoList = fornoImages.keys().map((image) => fornoImages(image));

  const snackImages = require.context("./images/snack", true);
  const snackList = snackImages.keys().map((image) => snackImages(image));

  const featuredServices = [
    {
      icon: Waves,
      title: "svcPoolTitle",
      description: "svcPoolDesc",
      image: pool,
      imgs: poolList,
      badge: "svcPoolBadge",
    },
    {
      icon: TreePine,
      title: "svcGardenBBQTitle",
      description: "svcGardenBBQDesc",
      image: bbq,
      imgs: bbqList,
    },
    {
      icon: Coffee,
      title: "svcCoffeeSnackTitle",
      description: "svcCoffeeSnackDesc",
      badge: "svcCoffeeSnackBadge",
      image: snack,
      imgs: snackList,
    },
    {
      icon: Flame,
      title: "svcWoodOvenTitle",
      description: "svcWoodOvenDesc",
      image: fornolegna,
      imgs: fornoList,
    },
  ];

  const additionalServices = [
    {
      icon: Coffee,
      title: "svcBreakfastTitle",
      description: "svcBreakfastDesc",
      badge: "svcBreakfastBadge",
      isSmall: true,
    },
    {
      icon: Car,
      title: "svcParkingTitle",
      description: "svcParkingDesc",
      isSmall: true,
    },
    {
      icon: Wifi,
      title: "svcWifiTitle",
      description: "svcWifiDesc",
      isSmall: true,
    },
    {
      icon: Wind,
      title: "svcAirConTitle",
      description: "svcAirConDesc",
      isSmall: true,
    },
    {
      icon: PawPrint,
      title: "svcPetFriendlyTitle",
      description: "svcPetFriendlyDesc",
      isSmall: true,
    },
    {
      icon: Gamepad2,
      title: "svcMovieNightTitle",
      description: "svcMovieNightDesc",
      isSmall: true,
    },
  ];

  return (
    <section
      id="servizi"
      className="relative py-16 sm:py-20 lg:py-24"
      style={{ backgroundColor: theme.cream }}
    >
      <div className="flex justify-center">
        <div className="flex items-center gap-3">
          <div className="w-12 h-0.5 bg-gradient-to-r from-transparent to-rose-300"></div>
          <div className="text-rose-300 text-2xl">❀</div>
          <div className="w-2 h-2 bg-rose-300 rounded-full"></div>
          <div className="text-rose-300 text-2xl">❀</div>
          <div className="w-12 h-0.5 bg-gradient-to-l from-transparent to-rose-300"></div>
        </div>
      </div>
      {/* Intro section before Services */}
      <div className="relative text-center mb-12 px-4 py-10 sm:mb-16 lg:mb-20 mx-auto">
        <div className="max-w-2xl mx-auto">
          <h2
            className="text-3xl sm:text-4xl font-bold mb-4"
            style={{ color: theme.moss }}
          >
            {t("introRelaxHeading")}
          </h2>
          <p className="text-lg my-10" style={{ color: theme.warmBrown }}>
            {t("introRelaxParagraph1")}
          </p>
          <p className="text-lg my-10" style={{ color: theme.warmBrown }}>
            {t("introRelaxParagraph2")}
          </p>
          <button
            onClick={() => (window.location.href = "/gallery")}
            className="px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:shadow-md"
            style={{
                  backgroundColor: theme.sage,
                  color: "white",
                }}
          >
            {t("btnGallery")}
          </button>
        </div>
        {/* Parallax decorative icons */}
        <Parallax
          speed={-15}
          className="hidden md:block absolute left-6 lg:left-10 top-5 pointer-events-none"
        >
          <Leaf size={56} style={{ color: theme.lightSage + "aa" }} />
        </Parallax>
        <Parallax
          speed={-15}
          className="hidden md:block absolute right-6 lg:right-10 top-5 pointer-events-none"
        >
          <Flower size={52} style={{ color: theme.lightSage}} />
        </Parallax>
        <Parallax
          speed={-15}
          className="hidden md:block absolute left-6 lg:left-10 bottom-5 pointer-events-none"
        >
          <TreePine size={60} style={{ color: theme.lightSage + "aa" }} />
        </Parallax>
        <Parallax
          speed={-15}
          className="hidden md:block absolute right-6 lg:right-10 bottom-5 pointer-events-none"
        >
          <Waves size={58} style={{ color: theme.lightSage + "aa" }} />
        </Parallax>
      </div>
      <StickyHeading
        stickyBg={theme.cream}
        observerOptions={{ rootMargin: "-64px 0px 0px 0px" }}
        className="flex items-center justify-center text-center p-3"
        stuckStyle={{ borderBottom: `1px solid ${theme.wheat}` }}
      >
        <h2
          className="text-4xl sm:text-5xl font-bold mx-auto"
          style={{ color: theme.moss }}
        >
          {t("headingServices")}
        </h2>
      </StickyHeading>
      <div className="relative max-w-7xl mx-auto lg:px-8">
        <div className="flex justify-center mb-12 sm:mb-16 lg:mb-20 z-[10]">
          <div className="text-center p-2 pt-0 sm:p-5">
            <p
              className="text-xl max-w-3xl mx-auto"
              style={{ color: theme.warmBrown }}
            >
              {t("servicesSubheading")}
            </p>
          </div>
        </div>
        <div>
          {/* Featured Services with Images */}
          <div className="px-0 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-0 md:gap-8 mb-8">
            {featuredServices.map((service, index) => (
              <ServiceCard
                key={index}
                {...service}
                title={t(service.title)}
                description={t(service.description)}
                badge={service.badge ? t(service.badge) : undefined}
              />
            ))}
          </div>
          {/* Additional Services */}
          <div className="px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 max-w-4xl mx-auto gap-6">
            {additionalServices.map((service, index) => (
              <ServiceCard
                key={index}
                {...service}
                title={t(service.title)}
                description={t(service.description)}
                badge={service.badge ? t(service.badge) : undefined}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Rooms Section
const RoomsSection = () => {
  const { t } = useTranslation();
  // Load all images for each room using require.context
  const pinkImgs = require
    .context("./images/rooms/rosa", false, /\.(jpe?g|png|webp)$/)
    .keys()
    .map((file) => require(`./images/rooms/rosa/${file.replace("./", "")}`));
  const brownImgs = require
    .context("./images/rooms/marrone", false, /\.(jpe?g|png|webp)$/)
    .keys()
    .map((file) => require(`./images/rooms/marrone/${file.replace("./", "")}`));
  const cyanImgs = require
    .context("./images/rooms/azzurra", false, /\.(jpe?g|png|webp)$/)
    .keys()
    .map((file) => require(`./images/rooms/azzurra/${file.replace("./", "")}`));
  const greenImgs = require
    .context("./images/rooms/verde", false, /\.(jpe?g|png|webp)$/)
    .keys()
    .map((file) => require(`./images/rooms/verde/${file.replace("./", "")}`));

  const rooms = [
    {
      name: "roomPinkName",
      image: pink,
      imgs: pinkImgs,
      features: [
        { icon: Home, label: "featureFirstFloor" },
        { icon: Users, label: "featureDoubleBed" },
        { icon: MapPin, label: "featureGardenBalcony" },
      ],
      additionalLabel: "roomPinkAdditionalLabel",
    },
    {
      name: "roomBrownName",
      image: brown,
      imgs: brownImgs,
      features: [
        { icon: Home, label: "featureFirstFloor" },
        { icon: Users, label: "featureDoubleAndBunk" },
        { icon: MapPin, label: "featureGardenBalcony" },
      ],
    },
    {
      name: "roomCyanName",
      image: cyan,
      imgs: cyanImgs,
      features: [
        { icon: Home, label: "featureFirstFloor" },
        { icon: Users, label: "featureDoubleAndBunk" },
        { icon: MapPin, label: "featureMountainView" },
      ],
      additionalLabel: "roomPinkAdditionalLabel",
    },
    {
      name: "roomGreenName",
      image: green,
      imgs: greenImgs,
      features: [
        { icon: Home, label: "featureGroundFloor" },
        { icon: Users, label: "featureDoubleBed" },
        { icon: Home, label: "featureIndependentEntrance" },
        { icon: MapPin, label: "featurePrivateOutdoorBalcony" },
      ],
    },
  ];

  return (
    <section
      id="camere"
      className="py-16 sm:py-20 lg:py-24 bg-transparent relative"
    >
      <div className="absolute inset-0 overflow-hidden h-full hidden">
        <Parallax speed={-100} className="absolute inset-0 pointer-events-none">
          <div className="w-full h-full dots-pattern"></div>
        </Parallax>
      </div>

      <div className="fixed inset-0 overflow-hidden h-full z-[-1] block">
        <div className="w-full h-full dots-pattern"></div>
      </div>

      <div
        className="absolute inset-x-0 top-0 h-[35rem] z-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, " +
            theme.cream +
            ", rgba(255,255,255,0))",
        }}
      ></div>

      <div
        className="absolute inset-x-0 bottom-0 h-[35rem] z-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(255,255,255,0), " +
            theme.cream +
            ")",
        }}
      ></div>
      <StickyHeading
        stickyBg={theme.cream}
        observerOptions={{ rootMargin: "-64px 0px 0px 0px" }}
        className="flex items-center justify-center text-center p-3"
        stuckStyle={{ borderBottom: `1px solid ${theme.wheat}` }}
      >
        <h2
          className="text-4xl sm:text-5xl font-bold mx-auto"
          style={{ color: theme.moss }}
        >
          {t("headingRooms")}
        </h2>
      </StickyHeading>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center mb-12 sm:mb-16 lg:mb-20 z-[10]">
          <div className="text-center p-2 pt-0 sm:p-5">
            <p
              className="text-xl max-w-3xl mx-auto"
              style={{ color: theme.warmBrown }}
            >
              {t("roomsSubheading")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {rooms.map((room, index) => (
            <RoomCard
              key={index}
              {...room}
              name={t(room.name)}
              additionalLabel={
                room.additionalLabel ? t(room.additionalLabel) : undefined
              }
              features={room.features.map((f) => ({ ...f, label: t(f.label) }))}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3">
          {/* Special Offer */}
          <div
            className="relative flex items-center justify-center rounded-3xl p-8 sm:p-12 text-white text-center shadow-lg overflow-hidden"
            style={{
              backgroundColor: theme.sage,
              borderBottom: "4px solid" + theme.moss,
            }}
          >
            <div className="relative z-10 flex flex-col items-center">
              <h3 className="text-3xl sm:text-4xl font-bold mb-6">
                {t("specialOfferHeading")}
              </h3>
              <p className="text-lg sm:text-xl mb-8 max-w-2xl mx-auto opacity-90">
                {t("specialOfferDesc")}
              </p>
              <div
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full"
                style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
              >
                <span className="text-sm">{t("specialOfferNote")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

};

// Private Event Section
const PrivateEventSection = () => {
  const { t } = useTranslation();

  // Load all event images for the gallery
  const eventImagesCtx = require.context("./images/events", true, /\.(jpe?g|png|webp)$/);
  const eventImages = eventImagesCtx.keys().map((img) => eventImagesCtx(img));

  return (
    <section
      id="eventi-privati"
      className="py-16 sm:py-20 lg:py-24 bg-transparent relative"
      style={{ backgroundColor: theme.cream }}
    >
      <StickyHeading
        stickyBg={theme.cream}
        observerOptions={{ rootMargin: "-64px 0px 0px 0px" }}
        className="flex items-center justify-center text-center p-3"
        stuckStyle={{ borderBottom: `1px solid ${theme.wheat}` }}
      >
        <h2
          className="text-4xl sm:text-5xl font-bold mx-auto"
          style={{ color: theme.moss }}
        >
          {t("specialOffer2Heading")}
        </h2>
      </StickyHeading>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center mb-12 sm:mb-16 lg:mb-20 z-[10]">
          <div className="text-center p-2 pt-0 sm:p-5">
            <p className="text-xl max-w-3xl mx-auto" style={{ color: theme.warmBrown }}>
              {t("specialOffer2Desc")}
            </p>
            <div
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full mt-6"
              style={{ backgroundColor: theme.blush, border: `1px solid ${theme.wheat}`, color: theme.warmBrown }}
            >
              <span className="text-sm">{t("specialOffer2Note")}</span>
            </div>
          </div>
        </div>

        {/* Photo gallery */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {eventImages.map((src, idx) => (
            <div
              key={idx}
              className="relative rounded-2xl overflow-hidden shadow-sm"
              style={{ border: `1px solid ${theme.wheat}` }}
            >
              <img
                src={src}
                alt={`event-${idx}`}
                className="w-full h-44 sm:h-56 md:h-64 object-cover transform transition-transform duration-300 hover:scale-[1.03]"
              />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex justify-center mt-12">
          <button
            onClick={() => document.getElementById("contatti")?.scrollIntoView({ behavior: "smooth" })}
            className="px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:shadow-md"
            style={{ backgroundColor: theme.sage, color: "white" }}
          >
            {t("Prenota")}
          </button>
        </div>
      </div>
    </section>
  );
}

const PeekingWindow = () => {
  return (
    <div
      className="relative h-[45vh] aspect-[3.5/5] rounded-sm mx-auto overflow-hidden"
      style={{ borderColor: theme.warmBrown }}
    >
      <div className="relative h-full w-full p-10 pb-20 overflow-hidden">
        <div className="relative border-2 border-blue-500 overflow-hidden h-full">
          <Parallax speed={-7}>
            <img src={fromWindow} className="h-full w-full scale-[3]"></img>
          </Parallax>

          <Parallax
            speed={-15}
            className="absolute inset-0 h-[150%] my-auto w-[150%]"
            style={{
              background:
                "linear-gradient(135deg, rgba(0, 0, 0, 0) 0%, rgba(255, 255, 255, 0.3) 24%, rgba(0, 0, 0, 0) 24%, rgba(0, 0, 0, 0) 42%, rgba(255, 255, 255, 0.3) 43%, rgba(255, 255, 255, 0.3) 47%, rgba(0, 0, 0, 0) 47%, rgba(0, 0, 0, 0) 56%, rgba(255, 255, 255, 0.3) 57%, rgba(0, 0, 0, 0) 87%)",
            }}
          />
        </div>
      </div>
      <img src={windowFrame} className="absolute inset-0 h-full w-full"></img>
    </div>
  );
};

const PeekingWindowOld = () => {
  return (
    <div
      className="relative h-56 aspect-[4/3] rounded-sm mx-auto border-[10px] overflow-hidden"
      style={{ borderColor: theme.warmBrown }}
    >
      <Parallax speed={-10}>
        <img src={fromWindow} className="h-full w-full scale-[5]"></img>
      </Parallax>

      <Parallax
        speed={-15}
        className="absolute inset-0 h-[150%] my-auto w-[150%]"
        style={{
          background:
            "linear-gradient(135deg, rgba(0, 0, 0, 0) 0%, rgba(255, 255, 255, 0.3) 24%, rgba(0, 0, 0, 0) 24%, rgba(0, 0, 0, 0) 42%, rgba(255, 255, 255, 0.3) 43%, rgba(255, 255, 255, 0.3) 47%, rgba(0, 0, 0, 0) 47%, rgba(0, 0, 0, 0) 56%, rgba(255, 255, 255, 0.3) 57%, rgba(0, 0, 0, 0) 87%)",
        }}
      />
      <div
        className="absolute inset-0 w-[10px] mx-auto h-full"
        style={{ backgroundColor: theme.warmBrown }}
      />
      <div
        className="absolute inset-0 h-[10px] my-auto w-full"
        style={{ backgroundColor: theme.warmBrown }}
      />
    </div>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useTranslation();
  return (
    <section
      id="contatti"
      className="py-20 relative overflow-hidden"
      style={{ backgroundColor: theme.cream }}
    >
      <PeekingWindow></PeekingWindow>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="mb-12">
          <h2
            className="text-4xl sm:text-5xl font-bold mb-6"
            style={{ color: theme.moss }}
          >
            {t("headingContact")}
          </h2>
          <p
            className="text-xl max-w-3xl mx-auto"
            style={{ color: theme.warmBrown }}
          >
            {t("contactSubheading")}
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* Contact Card */}
          <div
            className="rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden"
            style={{ border: `4px dashed ${theme.wheat}` }}
          >
            <div className="relative z-10">
              {/* Key Info */}
              <div className="grid grid-cols-2 gap-6 mb-8 text-left">
                <div className="flex-col items-center text-center align-center justify-center gap-4">
                  <MapPin
                    size={28}
                    style={{ color: theme.moss }}
                    className="mx-auto mb-1"
                  />
                  <h4 className="font-bold mb-1" style={{ color: theme.moss }}>
                    {t("contactLocation")}
                  </h4>
                  <p style={{ color: theme.warmBrown }}>
                    {t("contactLocationText")}
                  </p>
                  <p className="text-sm" style={{ color: theme.warmBrown }}>
                    {t("contactLocationNote")}
                  </p>
                </div>
                <div className="flex-col items-center text-center align-center justify-center gap-4">
                  <Calendar
                    size={28}
                    style={{ color: theme.moss }}
                    className="mx-auto mb-1"
                  />
                  <h4 className="font-bold mb-1" style={{ color: theme.moss }}>
                    {t("contactCheckin")}
                  </h4>
                  <p style={{ color: theme.warmBrown }}>
                    {t("contactCheckinTime")}
                  </p>
                  <p className="text-sm" style={{ color: theme.warmBrown }}>
                    {t("contactCheckoutTime")}
                  </p>
                </div>
              </div>

              {/* Phone Number Display */}
              <div className="mb-8">
                <div className="flex justify-center items-center gap-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: theme.wheat }}
                  >
                    <Phone size={24} style={{ color: theme.warmBrown }} />
                  </div>
                  <span
                    className="text-2xl font-bold"
                    style={{ color: theme.moss }}
                  >
                    {t("callPhoneNumber")}
                  </span>
                </div>
              </div>

              {/* Contact Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <ContactButton
                  type="whatsapp"
                  number={t("whatsappPhoneNumber")}
                />
                <ContactButton type="phone" number={t("callPhoneNumber")} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                <ContactButton
                  type="facebook"
                  number={t("facebookLink")}
                />
                <ContactButton type="instagram" number={t("instagramLink")} />
              </div>

              <div
                className="rounded-2xl p-6"
                style={{
                  backgroundColor: theme.blush,
                  border: `1px solid ${theme.wheat}`,
                }}
              >
                <p
                  className="flex items-center justify-center gap-2"
                  style={{ color: theme.warmBrown }}
                >
                  {t("promoWhatsapp")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main App Component
const App = () => {
  // Auto‑scroll when the page is loaded with a hash in the URL
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1); // remove '#'
      // Wait one tick to ensure the DOM is fully mounted
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 0);
    }
  }, []);
  return (
    <ParallaxProvider>
      <div className="min-h-screen bg-transparent">
        <Header />
        <HeroSection />
        <ServicesOverview />
        <RoomsSection />
        <PrivateEventSection />
        <ActivitiesSection />
        <ContactSection />
        <Footer />
      </div>
    </ParallaxProvider>
  );
};

export default App;
